import React, { useCallback, useState } from 'react';
import {
  AhaEditorV2,
  type AhaEditorV2Props,
  type Behaviors,
  type EditorPlugin,
  menus,
  ToolbarGroup,
  tools,
} from '@aha-app/aha-editor-v2';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import AhaNewRecordEditorController, {
  type AhaNewRecordEditorControllerProps,
} from './controllers/AhaNewRecordEditorController';
import ToolInsertTemplate from '../aha_editor_field_v2/plugins/InsertTemplate/tools/ToolInsertTemplate';
import AttachmentsController from '../aha_editor_field_v2/controllers/AttachmentsController';
import SanitizeInnerHTML from 'components/SanitizeInnerHTML/SanitizeInnerHTML';
import useStickyToolbar from 'components/aha_editor_field_v2/hooks/useStickyToolbar';
import classNames from 'classnames';

const commandMenuTools = [
  [
    tools.ToolToggleHeading1,
    tools.ToolToggleHeading2,
    tools.ToolToggleHeading3,
    tools.ToolToggleHeading4,
    tools.ToolToggleHeading5,
    tools.ToolToggleHeading6,
    tools.ToolToggleCode,
    tools.ToolToggleQuote,
  ],
  [tools.ToolBulletedList, tools.ToolNumberedList, tools.ToolCheckList],
  [tools.ToolInsertMedia, tools.ToolInsertLink, tools.ToolInsertTable],
  [
    tools.ToolToggleTableOfContents,
    tools.ToolInsertLabelPill,
    tools.ToolInsertInfoBlock,
    tools.ToolInsertNoteBlock,
    tools.ToolInsertTipBlock,
    tools.ToolInsertWarningBlock,
    tools.ToolInsertDividerLine,
  ],
];

const insertMenu = ToolbarGroup({
  menu: {
    icon: 'fa-regular fa-plus',
    name: 'Insert',
    tools: [
      [
        tools.ToolInsertMedia,
        tools.ToolInsertLink,
        tools.ToolInsertTable,
        ToolInsertTemplate,
        tools.ToolInsertEmbed,
      ],
      [tools.ToolCopyFormat, tools.ToolPasteFormat],
      [
        tools.ToolToggleTableOfContents,
        tools.ToolInsertLabelPill,
        tools.ToolInsertCalloutBlock,
        tools.ToolInsertDividerLine,
      ],
      [tools.ToolWordCount],
    ],
  },
});

const toolbar: AhaEditorV2Props['toolbar'] = {
  left: [
    menus.ToolbarBlockMenu(),
    tools.ToolBold,
    tools.ToolItalic,
    tools.ToolUnderline,
    menus.ToolbarFormattingMenu(),
    menus.ToolbarAlignmentMenu(),
    menus.ToolbarListMenu(),
    tools.ToolInsertEmoji,
    insertMenu,
    tools.ToolWritingAssistant,
  ],
  right: [tools.ToolFullScreen],
};

const compactToolbar: AhaEditorV2Props['compactToolbar'] = {
  left: [
    menus.ToolbarBlockMenu({ compact: true }),
    menus.ToolbarFormattingMenu({ compact: true }),
    menus.ToolbarAlignmentMenu(),
    menus.ToolbarListMenu(),
    tools.ToolInsertEmoji,
    insertMenu,
    tools.ToolWritingAssistant,
  ],
  right: [tools.ToolFullScreen],
};

export interface AhaNewRecordEditorProps
  extends AhaNewRecordEditorControllerProps {
  placeholderText?: string;
  readOnly?: boolean;
}

const AhaNewRecordEditor: React.FC<AhaNewRecordEditorProps> = ({
  placeholderElement,
  placeholderText,
  className,
  focusOnMount = false,
  readOnly = false,
}) => {
  const controller = useController(AhaNewRecordEditorController);
  const attachmentsController = useController(AttachmentsController);
  const toolbarOptions = useStickyToolbar(controller.container);

  // Re-render the editor once the AhaEditor is fully initialized, since that is after
  // element refs have been initialized
  const [, setInitialized] = useState(false);
  const [showPlaceholderElement, setShowPlaceholderElement] = useState(
    Boolean(placeholderElement)
  );

  const setContainer = useCallback(
    (container: HTMLDivElement) => {
      if (container) {
        attachmentsController.actionSetContainer(container);
        controller.setContainer(container);
      }
    },
    [controller, attachmentsController]
  );

  const [plugins] = useState(() => {
    const initPlugin: EditorPlugin = {
      name: 'AhaNewRecordEditor initialization plugin',
      handlers: {
        onMount(ahaEditor) {
          setInitialized(true);
        },
      },
    };

    return [
      ...controller.plugins,
      ...attachmentsController.plugins,
      initPlugin,
    ];
  });

  if (showPlaceholderElement && !focusOnMount) {
    return (
      <div
        className='aha-editor-placeholder tw-cursor-pointer'
        onClick={() => setShowPlaceholderElement(false)}
      >
        {placeholderElement}
      </div>
    );
  }

  const behaviors: Behaviors = {
    showBorder: true,
    minimumLines: 3,
    placeholder: placeholderText || '',
    placeholderChildren: placeholderText && (
      <SanitizeInnerHTML
        element={<span className='user-content' />}
        html={placeholderText}
      />
    ),
    // If there is a placeholderElement, then we want to focusOnMount since the editor wasn't actually rendered initially.
    focusOnMount: focusOnMount || !!placeholderElement,
  };

  return (
    <div
      ref={setContainer}
      className={classNames('aha-editor-field-v2', className)}
    >
      <AhaEditorV2
        plugins={plugins}
        value={controller.renderValue}
        readOnly={readOnly}
        interactive={!readOnly}
        toolbar={toolbar}
        compactToolbar={compactToolbar}
        toolbarOptions={toolbarOptions}
        commandMenuTools={commandMenuTools}
        behaviors={behaviors}
        serializerOptions={{
          allowedMediaRegex: controller.allowedMediaRegex,
          baseURL: window.location.origin,
        }}
      />
    </div>
  );
};

export default StartControllerScope(
  AhaNewRecordEditorController,
  StartControllerScope(
    AttachmentsController,
    ApplicationView(AhaNewRecordEditor)
  )
);
